// Eine Listen-Komponente, drei Sichten. Der mode entscheidet den Filter, nicht das Layout:
//   my        — freigegebene Auftraege in den Arbeitsplaetzen des Nutzers (was ein Mechaniker heute tut)
//   backlog   — alles Offene im Werk (Meister-Uebersicht)
//   approvals — CRTD-Auftraege, die auf Freigabe warten (Meister-Freigabe-Queue)
// Echte Personen-Zuordnung kommt mit echter Auth; der Mock naehert "meine" ueber Arbeitsplaetze an.
//
// Portierung aus dem Vite-Prototyp (frontend/src/components/OrdersList.jsx). Geaendert wurden NUR
// Styling-Idiom (v4-Token -> isDarkMode-Verzweigung), Theme-/i18n-Hook, i18n-Keys (pm.-Praefix) und
// die Import-/API-Pfade. Der StatusBadge wandert ins geteilte ui-Kit (./ui) und wird von dort
// importiert (statt hier exportiert wie im Prototyp).
//
// MONOLITH-API-KONVENTION: `user` zuerst, Antwort ist direkt der JSON-Body (.items).
import React, { useEffect, useState } from 'react';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { listOrders, myAssignments } from '../api';
import { Card, StatusBadge } from './ui';

// An order stays commercially open until TECO, but SAP raises the HEADER status to PCNF/CNF the
// moment an operation is confirmed — and `_primaryStatus` ranks CNF/PCNF above REL, so the
// collapsed status of a booked-but-open order is PCNF, never REL. Testing for REL alone therefore
// made an order vanish from both lists as soon as anyone booked time on it (PM code audit 04).
// Same predicate duplicated in components/reports/EquipmentTab.js (open-orders KPI) — change both.
const OPEN_STATUS = ['CRTD', 'REL', 'PCNF', 'CNF'];
// "my" is the released half of that set: a CRTD order is not yet the mechanic's work.
const MY_STATUS = ['REL', 'PCNF', 'CNF'];

// "my" = person assignment (portal-owned) FIRST, work-center approximation as fallback —
// an order explicitly assigned to me is mine regardless of its work center.
const MODES = {
  my:        { titleKey: 'pm.orders.myTitle',        filter: (o, scope, mine) => mine.has(o.orderId) || (MY_STATUS.includes(o.status) && scope.workCenters.includes(o.workCenter)) },
  backlog:   { titleKey: 'pm.orders.backlogTitle',   filter: (o) => OPEN_STATUS.includes(o.status) },
  // approvals stays exact-CRTD: only a not-yet-released order is waiting for a release decision.
  approvals: { titleKey: 'pm.orders.approvalsTitle', filter: (o) => o.status === 'CRTD' },
};

/** Age in days from createdAt; null when unknown. */
const ageDays = (o) => (o.createdAt ? Math.floor((Date.now() - Date.parse(o.createdAt)) / 86400000) : null);

function OrdersList({ me, user, mode, onOpen, onBack }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [orders, setOrders] = useState(null);
  const [loadError, setLoadError] = useState(false);
  // The SAP row cap cut the window: these rows are a slice, not the plant. Saying so beats
  // letting a short list read as the whole truth (PM code audit finding 06).
  const [truncated, setTruncated] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const cfg = MODES[mode] || MODES.my;

  useEffect(() => {
    // alive-Guard: bei schnellem Moduswechsel darf eine verspaetete Antwort des ALTEN Requests
    // die Liste des neuen Modus nicht ueberschreiben (Effekt-Cleanup entwertet den alten Request).
    let alive = true;
    setOrders(null);
    setLoadError(false);
    // OrdersList laedt ueber das AKTIVE Werk (me.activePlant, fail-closed aus dem Profil-Scope);
    // der mode-Filter approximiert "meine" via workCenters. Rolle mitschicken, damit das
    // serverseitige Scope-Gate gegen dasselbe Profil prueft wie die UI.
    Promise.all([
      listOrders(user, { plant: me.activePlant }, me.profile.id),
      mode === 'my' ? myAssignments(user, me.profile.id).catch(() => ({ orderIds: [] })) : Promise.resolve({ orderIds: [] }),
    ])
      .then(([res, mine]) => {
        if (!alive) return;
        const mineSet = new Set(mine.orderIds || []);
        let rows = (res?.items || []).filter((o) => cfg.filter(o, me.profile.scope, mineSet));
        // The backlog conversation starts with "what has been open longest" — oldest first.
        if (mode === 'backlog') rows = [...rows].sort((a, b) => (ageDays(b) ?? -1) - (ageDays(a) ?? -1));
        setTruncated(res?.truncated === true);
        setOrders(rows);
      })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, me, user, reloadKey]);

  return (
    <div>
      <button
        onClick={onBack}
        className={`flex items-center gap-1 mb-3 font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
      >
        <ArrowLeft size={18} /> {t('pm.common.back')}
      </button>
      <h2 className={`text-xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t(cfg.titleKey)}</h2>
      {mode === 'approvals' && (
        <p className={`text-sm mb-3 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('pm.orders.approvalsHint')}</p>
      )}

      {loadError ? (
        <div className="py-10 text-center space-y-3">
          <p className={isDarkMode ? 'text-red-300' : 'text-red-600'}>{t('pm.common.loadError')}</p>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className={`text-sm font-medium underline ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
          >
            {t('pm.common.retry')}
          </button>
        </div>
      ) : orders === null ? (
        <p className={`py-10 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.common.loading')}</p>
      ) : orders.length === 0 ? (
        <p className={`py-10 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.orders.empty')}</p>
      ) : (
        <div className="space-y-2 mt-2">
          {truncated && (
            <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
              {t('pm.orders.truncated')}
            </p>
          )}
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
            {orders.length} {orders.length === 1 ? t('pm.orders.countOne') : t('pm.orders.countSuffix')}
          </p>
          {orders.map((o) => (
            <button key={o.orderId} onClick={() => onOpen(o.orderId)} className="w-full text-left">
              <Card className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{o.description}</div>
                  <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                    {o.orderId} · {o.workCenter}{o.plannedStart ? ` · ${o.plannedStart}` : ''}
                    {o.maintenancePlan && (
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded text-xs font-semibold ${isDarkMode
                        ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-700'}`}>
                        {t('pm.badge.preventive')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {mode === 'backlog' && ageDays(o) !== null && (
                    <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${ageDays(o) > 30
                      ? (isDarkMode ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-100 text-rose-700')
                      : ageDays(o) > 7
                        ? (isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700')
                        : (isDarkMode ? 'bg-white/[0.08] text-white/50' : 'bg-slate-100 text-slate-500')}`}>
                      {ageDays(o)}d
                    </span>
                  )}
                  <StatusBadge status={o.status} />
                  <ChevronRight className={isDarkMode ? 'text-white/40' : 'text-slate-400'} size={18} />
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrdersList;
